import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  FormControl,
  FormLabel,
  SimpleGrid,
  Text,
  Select,
  Flex,
  Icon,
  IconButton,
  useDisclosure,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { FaPlus } from 'react-icons/fa';
import { CloseIcon } from '@chakra-ui/icons';
import CommonInput from 'components/commonInput/CommonInput';
import CommonSubmitBtn from 'components/commonInput/CommonSubmitBtn';
import CommonSwitchInput from 'components/commonInput/CommonSwitchInput';
import CommonReactSelect from 'components/reactSelectCommon/CommonReactSelect';
import PageLoader from 'components/wholePageLoader/PageLoader';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useColorModeVariables } from 'components/colorModeVarialbels/ColorModeValues';
import { couponAddEditValidation } from 'utils/CommonValidation';
import {
  AddCouponData,
  UpdateCouponData,
  ViewCouponData,
} from '../../../reactRedux/thunks/couponThunk/couponThunk';
import { ListMerchantData } from '../../../reactRedux/thunks/merchnatThunk/merchantThunk';
import useRegions from 'hooks/useRegions';
import MerchantListModal from '../stationPages/MerchantListModal';
import UserListModal from './UserListModal';
import moment from 'moment';
import { sideBarRoutes } from 'utils/StaticData';
const CouponAddEdit = () => {
  const { editCouponId } = useParams();
  const { regions: regionOptions } = useRegions();
  const { textColor, brandStars } = useColorModeVariables();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { onOpen, isOpen, onClose } = useDisclosure();
  const {
    onOpen: onUserModalOpen,
    isOpen: isUserModalOpen,
    onClose: onUserModalClose,
  } = useDisclosure();

  // Merchant selection state
  const [selectedMerchants, setSelectedMerchants] = useState([]);
  const [selectedMerchantName, setSelectedMerchantName] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [selectedMerchantObjects, setSelectedMerchantObjects] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(1); // Default to Netherlands
  const [isRemove, setIsRemove] = useState(false);

  // User selection state
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedUserName, setSelectedUserName] = useState('');

  const { isLoading, viewCouponDetails, isViewLoading } = useSelector(
    (state) => state.CouponSlice,
  );

  const { adminDetails } = useSelector((state) => state.authSlice);

  const {
    handleSubmit,
    setValue,
    register,
    watch,
    formState: { errors },
    control,
    reset,
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(couponAddEditValidation),
    defaultValues: {
      couponCode: '',
      displayText: '', // <-- Add this line
      description: '',
      couponType: 'percentage',
      couponValue: '',
      merchantIds: [],
      userIds: [],
      regionId: 1, // Default to Netherlands (ID: 1)
      startDate: '',
      endDate: '',
      isActive: true,
      isHighlight: false,
      highlightPrice: 0,
      highlightLabel: '',
      maxUsers: '',
      maxUsesPerUser: '',
      maxUsesPerDay: '',
    },
  });

  const watchedcouponType = watch('couponType');
  const watchedRegionId = watch('regionId');

  const selectedRegionCurrencySymbol =
    regionOptions.find((r) => r.id === watchedRegionId)?.currencySymbol ?? '€';

  // Coupon type options
  const couponTypeOptions = [
    { value: 'percentage', label: 'Percentage (%)' },
    { value: 'fixed', label: `Fixed Amount (${selectedRegionCurrencySymbol})` },
    { value: 'time', label: 'Minutes' },
  ];

  // Fetch merchant data
  useEffect(() => {
    dispatch(ListMerchantData({ limit: 10 }));
  }, [dispatch]);

  // Load coupon data for edit mode
  useEffect(() => {
    if (editCouponId) {
      dispatch(ViewCouponData(editCouponId));
    }
  }, [editCouponId, dispatch]);

  // Sync selectedRegion with form regionId
  useEffect(() => {
    if (watchedRegionId) {
      setSelectedRegion(watchedRegionId);
    }
  }, [watchedRegionId]);

  // Populate form when editing
  useEffect(() => {
    if (editCouponId && viewCouponDetails) {
      const couponData = viewCouponDetails.data || viewCouponDetails;

      // Helper function to format date for input
      const formatDateForEdit = (dateString) => {
        if (!dateString) return '';

        return moment.parseZone(dateString).format('YYYY-MM-DD');
      };
      // Set merchant selection state for edit mode
      if (couponData.merchants && couponData.merchants.length > 0) {
        const merchantIds = couponData.merchants.map((item) => item.merchantId);
        const merchantNames = couponData.merchants
          .map((item) => item.name || `Merchant ${item.merchantId}`)
          .join('||');
        setSelectedMerchants(merchantIds);
        setSelectedMerchantName(merchantNames);
      }

      if (couponData.users && couponData.users.length > 0) {
        const userIds = couponData.users.map((item) => item.userId);
        const userNames = couponData.users
          .map((item) => `${item.firstName} ${item.lastName}`)
          .join('||');
        setSelectedUsers(userIds);
        setSelectedUserName(userNames);
      }

      reset({
        couponCode: couponData.couponCode || '',
        displayText: couponData.displayText || '', // <-- Add this line
        description: couponData.description || '',
        couponType: couponData.couponType || 'percentage',
        couponValue: couponData.couponValue || '',
        merchantIds: couponData.merchants?.map((item) => item.merchantId) || [],
        userIds: couponData.users?.map((item) => item.userId) || [],
        regionId: couponData.regionId || 1,
        startDate: formatDateForEdit(couponData.startDate),
        endDate: formatDateForEdit(couponData.endDate),
        isActive:
          couponData.isActive !== undefined ? couponData.isActive : true,
        isHighlight:
          couponData.isHighlight !== undefined ? couponData.isHighlight : false,
        highlightPrice: Number(couponData.highlightPrice || 0),
        highlightLabel: couponData.highlightLabel || '',
        maxUsers: couponData.maxUsers || '',
        maxUsesPerUser: couponData.maxUsesPerUser || '',
        maxUsesPerDay: couponData.dailyLimit || '',
      });
    }
  }, [viewCouponDetails, editCouponId, reset]);

  const onSubmit = (data) => {
    // Handle dates - send as date strings without timezone conversion
    const startDate = data.startDate || null;
    const endDate = data.endDate || null;

    const payload = {
      couponCode: data.couponCode,
      displayText: data.displayText, // <-- Add this line
      description: data.description,
      couponType: data.couponType,
      couponValue: Number(data.couponValue),
      merchantIds: selectedMerchants || [],
      userIds: selectedUsers || [],
      regionId:
        adminDetails?.role === sideBarRoutes?.Dealer && adminDetails?.regionId
          ? adminDetails.regionId // Auto-set dealer's region
          : data.regionId || 1, // Use selected region
      startDate: startDate,
      endDate: endDate,
      isActive: data.isActive,
      isHighlight: data.isHighlight,
      highlightPrice: data.isHighlight ? Number(data.highlightPrice) : 0,
      highlightLabel: data.isHighlight ? data.highlightLabel : '',
      maxUsers: Number(data.maxUsers),
      maxUsesPerUser: Number(data.maxUsesPerUser),
      dailyLimit: Number(data.maxUsesPerDay),
    };

    if (editCouponId) {
      dispatch(UpdateCouponData(editCouponId, payload, navigate));
    } else {
      dispatch(AddCouponData(payload, navigate));
    }
  };

  // Remove merchant function
  const handleRemoveMerchant = (indexToRemove) => {
    const updatedMerchantIds = [...selectedMerchants];
    updatedMerchantIds.splice(indexToRemove, 1);

    const merchantNamesArray = selectedMerchantName.split('||');
    const updatedMerchantNames = merchantNamesArray.filter(
      (_, index) => index !== indexToRemove,
    );

    if (updatedMerchantIds.length === 0) {
      setSelectedMerchantName('');
      setSelectedMerchants([]);
      setValue('merchantId', []);
    } else {
      setSelectedMerchantName(updatedMerchantNames.join('||'));
      setSelectedMerchants(updatedMerchantIds);
      setValue('merchantId', updatedMerchantIds);
    }
  };

  // Remove user function
  const handleRemoveUser = (indexToRemove) => {
    const updatedUserIds = [...selectedUsers];
    updatedUserIds.splice(indexToRemove, 1);

    const userNamesArray = selectedUserName.split('||');
    const updatedUserNames = userNamesArray.filter(
      (_, index) => index !== indexToRemove,
    );

    if (updatedUserIds.length === 0) {
      setSelectedUserName('');
      setSelectedUsers([]);
    } else {
      setSelectedUserName(updatedUserNames.join('||'));
      setSelectedUsers(updatedUserIds);
    }
  };

  const handleRegionChange = (selectedOption) => {
    // Handle single region selection
    const regionId = selectedOption ? Number(selectedOption.id) : 1;
    setValue('regionId', regionId, { shouldValidate: true });

    // Update selectedRegion state
    setSelectedRegion(selectedOption ? selectedOption.id : 1);

    // Clear selected merchants when region changes
    setSelectedMerchants([]);
    setSelectedMerchantName('');
    setSelectedMerchantObjects([]);
    setValue('merchantId', []);
  };

  if (isViewLoading) {
    return <PageLoader />;
  }

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Card
        w="100%"
        boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
        borderRadius="20px"
        paddingInline="40px"
        paddingBlock="50px"
      >
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap="20px" mb="20px">
            {/* Coupon Code */}
            <CommonInput
              labelText="Coupon Code"
              textColor={textColor}
              brandStars={brandStars}
              placeholder="e.g., SUMMER2024"
              register={{
                ...register('couponCode'),
                onChange: (e) => {
                  const upperCaseValue = e.target.value.toUpperCase();
                  setValue('couponCode', upperCaseValue);
                },
              }}
              errors={errors.couponCode}
              name="couponCode"
              type="text"
            />

            {/* Display Text */}
            <CommonInput
              labelText="Display Text"
              textColor={textColor}
              brandStars={brandStars}
              placeholder="Enter display text"
              register={register('displayText')}
              errors={errors.displayText}
              name="displayText"
              type="text"
              isValidate={true}
              isRequired={true}
            />

            {/* Description */}
            <CommonInput
              labelText="Description"
              textColor={textColor}
              brandStars={brandStars}
              placeholder="Enter coupon description"
              register={register('description')}
              errors={errors.description}
              name="description"
              type="text"
              isValidate={false}
            />

            {/* Coupon Type */}
            <FormControl>
              <FormLabel
                display="flex"
                ms="4px"
                fontSize="sm"
                fontWeight="500"
                color={textColor}
                mb="8px"
              >
                Coupon Type
                <Text color={brandStars}>*</Text>
              </FormLabel>
              <Select
                variant="auth"
                fontSize="sm"
                fontWeight="500"
                size="lg"
                borderRadius="16px"
                {...register('couponType')}
              >
                {couponTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
              {errors.couponType && (
                <Text color="red" fontSize="14px">
                  {errors.couponType.message}
                </Text>
              )}
            </FormControl>

            {/* Coupon Value */}
            <CommonInput
              labelText={`Coupon Value ${
                watchedcouponType === 'percentage'
                  ? '(%)'
                  : watchedcouponType === 'fixed'
                    ? `(${selectedRegionCurrencySymbol})`
                    : watchedcouponType === 'time'
                      ? '(mins)'
                      : ''
              }`}
              textColor={textColor}
              brandStars={brandStars}
              placeholder={
                watchedcouponType === 'percentage'
                  ? 'e.g., 10'
                  : watchedcouponType === 'fixed'
                    ? 'e.g., 5.99'
                    : watchedcouponType === 'time'
                      ? 'e.g., 30'
                      : 'e.g., 10'
              }
              register={register('couponValue')}
              errors={errors.couponValue}
              name="couponValue"
              type="number"
              isAddPercentage={watchedcouponType === 'percentage'}
              prefixText={
                watchedcouponType === 'fixed'
                  ? selectedRegionCurrencySymbol
                  : undefined
              }
            />

            {/* Start Date */}
            <FormControl>
              <CommonInput
                type="date"
                name="startDate"
                labelText="Start Date"
                textColor={textColor}
                brandStars={brandStars}
                placeholder="Select Date and Time"
                register={register('startDate')}
                errors={errors?.startDate}
                isValidate={false}
                isRequired={false}
              />
            </FormControl>

            {/* End Date */}
            <FormControl>
              <CommonInput
                type="date"
                name={`endDate`}
                labelText="End Date"
                textColor={textColor}
                brandStars={brandStars}
                placeholder="Select Date"
                register={register(`endDate`)}
                errors={errors?.endDate}
                isValidate={false}
                isRequired={false}
              />
            </FormControl>

            {/* Maximum Users */}
            <CommonInput
              labelText="Maximum Users"
              textColor={textColor}
              brandStars={brandStars}
              placeholder="e.g., 100"
              register={register('maxUsers')}
              errors={errors.maxUsers}
              name="maxUsers"
              type="number"
              isValidate={false}
            />

            {/* Maximum Uses Per User */}
            <CommonInput
              labelText="Max Uses Per User"
              textColor={textColor}
              brandStars={brandStars}
              placeholder="e.g., 5"
              register={register('maxUsesPerUser')}
              errors={errors.maxUsesPerUser}
              name="maxUsesPerUser"
              type="number"
              isValidate={false}
            />

            {/* Maximum Uses Per Day */}
            <CommonInput
              labelText="Daily Limit"
              textColor={textColor}
              brandStars={brandStars}
              placeholder="e.g., 10"
              register={register('maxUsesPerDay')}
              errors={errors.maxUsesPerDay}
              name="maxUsesPerDay"
              type="number"
              isValidate={false}
            />

            {/* Region Selection - Hidden for Dealers */}
            {adminDetails?.role !== sideBarRoutes?.Dealer && (
              <Controller
                name="regionId"
                control={control}
                render={({ field, fieldState: { error } }) => {
                  // Convert stored ID back to option object for display (single select)
                  const selectedRegion = field.value
                    ? regionOptions.find(
                        (option) => Number(option.id) === Number(field.value),
                      )
                    : null;

                  return (
                    <CommonReactSelect
                      label="Select Region"
                      type="select"
                      options={regionOptions}
                      value={selectedRegion} // Pass single selected option object
                      onChange={handleRegionChange}
                      error={error}
                      register={() => {}}
                      name="regionId"
                      getOptionLabel={(option) => option.label}
                      getOptionValue={(option) => option.id}
                      isMulti={false}
                      isRequired={true}
                      placeholderValue="Select region"
                    />
                  );
                }}
              />
            )}
          </SimpleGrid>

          {/* Active Status */}
          <Box mb="20px">
            <CommonSwitchInput
              labelText="Active Status"
              textColor={textColor}
              name="isActive"
              control={control}
              errors={errors.isActive}
              defaultChecked={true}
              isValidate={false}
            />
          </Box>

          {/* Is Highlight */}
          <Box mb="20px">
            <CommonSwitchInput
              labelText="Is Highlight"
              textColor={textColor}
              name="isHighlight"
              control={control}
              errors={errors.isHighlight}
              defaultChecked={false}
              isValidate={false}
            />
          </Box>

          {watch('isHighlight') && (
            <SimpleGrid
              columns={{ base: 1, md: 2, lg: 3 }}
              gap="20px"
              mb="20px"
            >
              {/* Highlight Price */}
              <CommonInput
                labelText="Price"
                textColor={textColor}
                brandStars={brandStars}
                placeholder="e.g., 10"
                register={register('highlightPrice')}
                errors={errors.highlightPrice}
                name="highlightPrice"
                type="number"
                isValidate={false}
              />

              {/* Highlight Label */}
              <CommonInput
                labelText="Label"
                textColor={textColor}
                brandStars={brandStars}
                placeholder="e.g., Code Applied"
                register={register('highlightLabel')}
                errors={errors.highlightLabel}
                name="highlightLabel"
                type="text"
                isValidate={false}
              />
            </SimpleGrid>
          )}

          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap="20px" mb="20px">
            {/* Merchant Selection */}
            <Box mt={7}>
              <Button
                type="button"
                bg="reviveOrange"
                h="50"
                w="100%"
                color="primary"
                borderRadius="12px"
                _hover={{ bg: 'reviveOrange', opacity: 0.8 }}
                onClick={onOpen}
              >
                <Icon
                  as={FaPlus}
                  width="15px"
                  height="15px"
                  borderRadius="12px"
                  marginRight="5px"
                  color="inherit"
                />
                Add Merchant
              </Button>
              {errors.merchantId && (
                <p style={{ color: 'red', marginTop: '5px' }}>
                  {errors.merchantId.message}
                </p>
              )}
            </Box>
            {/* User Selection */}
            <Box mt={7}>
              <Button
                type="button"
                bg="reviveOrange"
                h="50"
                w="100%"
                color="primary"
                borderRadius="12px"
                _hover={{ bg: 'reviveOrange', opacity: 0.8 }}
                onClick={onUserModalOpen}
              >
                <Icon
                  as={FaPlus}
                  width="15px"
                  height="15px"
                  borderRadius="12px"
                  marginRight="5px"
                  color="inherit"
                />
                Add User
              </Button>
            </Box>
          </SimpleGrid>

          {/* Merchant List */}
          {selectedMerchants?.length > 0 && (
            <Box>
              <FormLabel
                color={textColor}
                fontSize="lg"
                fontWeight="semibold"
                mb={2}
              >
                Merchants
              </FormLabel>

              <Flex flexWrap="wrap">
                {(Array.isArray(selectedMerchants)
                  ? selectedMerchantName
                      .split('||')
                      .filter((name) => name.trim() !== '')
                  : [selectedMerchantName].filter(
                      (name) => name && name.trim() !== '',
                    )
                ).map((name, index) => (
                  <Box key={index} width="100%">
                    {/* Merchant Name and Close button row */}
                    <Flex
                      alignItems="center"
                      justifyContent="space-between"
                      gap={3}
                    >
                      {/* Merchant Name */}
                      <FormLabel
                        color={textColor}
                        fontSize="md"
                        mb={0}
                        flex="1"
                      >
                        {name.trim()}
                      </FormLabel>

                      {/* Close button */}
                      <IconButton
                        aria-label={`Remove ${name}`}
                        icon={<CloseIcon />}
                        size="sm"
                        colorScheme="red"
                        onClick={() => handleRemoveMerchant(index)}
                        variant="ghost"
                      />
                    </Flex>
                  </Box>
                ))}
              </Flex>
            </Box>
          )}

          {/* User List */}
          {selectedUsers?.length > 0 && (
            <Box>
              <FormLabel
                color={textColor}
                fontSize="lg"
                fontWeight="semibold"
                mb={2}
                mt={5}
              >
                Users
              </FormLabel>

              <Flex flexWrap="wrap">
                {(Array.isArray(selectedUsers)
                  ? selectedUserName
                      .split('||')
                      .filter((name) => name.trim() !== '')
                  : [selectedUserName].filter(
                      (name) => name && name.trim() !== '',
                    )
                ).map((name, index) => (
                  <Box key={index} width="100%">
                    <Flex
                      alignItems="center"
                      justifyContent="space-between"
                      gap={3}
                    >
                      <FormLabel
                        color={textColor}
                        fontSize="md"
                        mb={0}
                        flex="1"
                      >
                        {name.trim()}
                      </FormLabel>

                      <IconButton
                        aria-label={`Remove ${name}`}
                        icon={<CloseIcon />}
                        size="sm"
                        colorScheme="red"
                        onClick={() => handleRemoveUser(index)}
                        variant="ghost"
                      />
                    </Flex>
                  </Box>
                ))}
              </Flex>
            </Box>
          )}

          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing="24px">
            <CommonSubmitBtn
              BTNtext={editCouponId ? 'Update Coupon' : 'Create Coupon'}
              isLoading={isLoading}
            />
          </SimpleGrid>

          {selectedRegion && (
            <MerchantListModal
              isOpen={isOpen}
              onClose={onClose}
              setSelectedMerchants={setSelectedMerchants}
              setSelectedMerchantName={setSelectedMerchantName}
              setSelectedMerchantObjects={setSelectedMerchantObjects}
              setValue={setValue}
              roleId={adminDetails.roleId}
              setIsRemove={setIsRemove}
              isRemove={isRemove}
              currentSelectedMerchants={
                Array.isArray(selectedMerchants) ? selectedMerchants : []
              }
              currentSelectedMerchantNames={selectedMerchantName}
              selectedRegion={selectedRegion}
              setSelectedRegion={setSelectedRegion}
              adminDetails={adminDetails}
              forceCheckbox={true}
              forceRegionFilter={true}
            />
          )}

          {isUserModalOpen && (
            <UserListModal
              isOpen={isUserModalOpen}
              onClose={onUserModalClose}
              setSelectedUsers={setSelectedUsers}
              setSelectedUserName={setSelectedUserName}
              setValue={setValue}
              currentSelectedUsers={
                Array.isArray(selectedUsers) ? selectedUsers : []
              }
              currentSelectedUserNames={selectedUserName}
            />
          )}
        </form>
      </Card>
    </Box>
  );
};

export default CouponAddEdit;
